import { AddCircleRounded, RemoveCircleRounded } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { gridLayout } from '../SectionLayout';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoDialogIcon, OptionalControl, Price } from '..';
import { AppState } from '../../store';
import { useSelector } from 'react-redux';
import {
  getNameWithCode,
  hasCompatibleValues,
  isVariableVisible,
  checkIsMarkedOptional,
  getFeatureTooltip,
} from '../../services/ConfigurationDataHelperFunctions';
import {
  blockInvalidChar,
  getButtonProperties,
  getLower,
  getMinValue,
  getUpper,
  getValidationInfo,
  isValidAssignment,
  onValueUpdate,
} from '../../services/NumericHelperFunctions';
import { useDebounce } from '../../hooks/Debounce';
import { DefaultIcon } from '../DefaultIcon';
import { hasDefaultValue } from '../../services/VariableAssignmentsTableHelperFunctions';

import type { IFeatureOrFamily, INumberControlProps } from '../../../types';

/**
 * Renders an input box for multi value numeric values.
 * Used for variables which allows the user to set custom numeric values
 * @param {INumberControlProps} props the properties for the numeric input component
 * @returns {JSX.Element} the numeric input component
 */
export const NumberControl = ({
  featureFlags,
  variable,
  value,
  onChange,
  subModelIcon,
  disabled,
  onOptionalChange,
}: INumberControlProps) => {
  const { t } = useTranslation();

  const [internalNumericValue, setInternalNumericValue] = useState(value);
  const [rangeValidity, setRangeValidity] = useState('');
  const [valueNotUpdated, setValueNotUpdated] = useState(false);

  //const values
  const step = 1;
  const isSubModel = !!subModelIcon;
  const lower = getLower(variable.values);
  const upper = getUpper(variable.values);
  const upperValue = upper.toString()?.length;
  const backgroundClass = hasCompatibleValues(variable) ? '' : 'incompatible';
  //state
  const showCode = useSelector((state: AppState) => state.userSettings.showCode);
  const featureLabel = getNameWithCode(variable as IFeatureOrFamily, showCode);
  const readOnlyTooltipText = t('tooltip.readOnlyTooltipText');
  const isOptional = checkIsMarkedOptional(variable.id ?? '', variable);
  const assignedChecked = isOptional ? false : !!value;

  // Synchronize internal value with external value
  useEffect(() => {
    setInternalNumericValue(value);
  }, [value]);

  // Calculate validation status
  const validity = useMemo(() => {
    if (internalNumericValue === value && !isOptional) {
      return { valid: true, validValues: [`[${lower} - ${upper}]`] };
    }
    return getValidationInfo(variable, `${internalNumericValue}`, isOptional);
  }, [internalNumericValue, value, variable, lower, upper, isOptional]);

  // Calculate validation hint text
  const validityHintText = useMemo(
    () =>
      t('errorMessages.numberInvalid', {
        value: internalNumericValue,
        range: validity.validValues.join(', '),
      }),
    [t, internalNumericValue, validity.validValues]
  );

  // Memoize button properties calculation
  const buttonProps = useMemo(
    () =>
      getButtonProperties({
        value: internalNumericValue ?? '',
        lower,
        upper,
        step,
        disabled,
      }),
    [internalNumericValue, lower, upper, step, disabled]
  );
  const isFeatureReadOnly = disabled || (buttonProps.inc.disabled && buttonProps.dec.disabled);
  const featureTooltip = getFeatureTooltip(variable, isFeatureReadOnly, showCode);

  /**
   * This methods check wheteher the value is valid or not and update to the state
   * @param { string } newValue
   */
  const onInternalChange = (newValue: string) => {
    //validate the current value (isOptional forwarded so 0/empty is rejected upstream)
    const validAssignment = getValidationInfo(variable, `${newValue}`, isOptional).valid;

    //check whether the value should update to the state or not
    const shouldUpdate = isValidAssignment(`${value}`, `${newValue}`, validAssignment);

    //Call onChange method to call the configure api call
    if (shouldUpdate) {
      onChange(`${newValue}`);
    }
  };

  //call debounce hook method to set the timeout
  const debounceOnChange = useDebounce(onInternalChange, 1000);

  /**
   *This method triggers when enter values in the text box
   * @param {React.ChangeEvent<HTMLInputElemen>} e
   */
  const onEventChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setRangeValidity('');
    setInternalNumericValue(e.target.value);
    debounceOnChange(e.target.value);
  };

  /**
   * This method triggers when focus leaves the component (click away or blur events)
   * @param {MouseEvent | TouchEvent | React.FocusEvent} e event object
   * @returns {void}
   */
  const onLeave = (e: MouseEvent | TouchEvent | React.FocusEvent): void => {
    setRangeValidity('');
    const relatedTarget = 'relatedTarget' in e ? e.relatedTarget : null;
    const isInvalid = !getValidationInfo(variable, `${internalNumericValue}`, isOptional).valid;
    if (isInvalid && !relatedTarget) {
      setValueNotUpdated(!valueNotUpdated);
    }
  };

  /**
   * This method triggers when check or uncheck the check box to update the numeric textbox value
   * @param {React.SyntheticEvent} e  select event
   * @returns {void}
   */
  const OnValueSelect = (e: React.SyntheticEvent): void => {
    // Cast target to HTMLInputElement since we know this event comes from a checkbox
    // EventTarget doesn't have 'checked' property, but HTMLInputElement does
    const target = e.target as HTMLInputElement;
    if (target?.checked) {
      //geth the minimum valid value to the text box
      //ex:If valid values are [0, 1-9999] then update value 1 to the text box
      const lowerValue = getMinValue(variable, 0);
      onInternalChange(lowerValue.toString());
    } else {
      //when uncheck the checkbox remove the value from the text box
      onInternalChange('');
    }
  };

  if (!isVariableVisible(variable)) {
    return null;
  }

  return (
    <>
      <ClickAwayListener onClickAway={onLeave}>
        <Grid
          key={variable.id}
          container
          className={`${backgroundClass} pt-2`}
          sx={{ alignItems: 'stretch' }}
          size={gridLayout.fullWidth}
        >
          <Grid
            className="clickAway feature-label-grid"
            container
            size={{
              xs: gridLayout.xs.input,
              sm: gridLayout.sm.input,
            }}
          >
            {/* Hide default icon when optional (SM2 removes default indicator) */}
            <DefaultIcon show={!!hasDefaultValue(variable) && !isOptional} />
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{featureTooltip}</span>}>
              <Box sx={{ display: 'flex' }}>
                <FormControlLabel
                  className={variable.isSearchValueMatched ? 'SearchValueMatched' : ''}
                  label={featureLabel}
                  checked={assignedChecked}
                  onChange={OnValueSelect}
                  control={<Checkbox className="paddingTopBottom" />}
                  disabled={isFeatureReadOnly || isOptional}
                  sx={{
                    alignItems: 'center',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    flex: 1,
                    // Keep label text fully visible when disabled due to optional state
                    ...(isOptional && {
                      '& .MuiFormControlLabel-label.Mui-disabled': {
                        color: 'inherit',
                        opacity: 1,
                      },
                    }),
                  }}
                />
                {isSubModel && subModelIcon()}
              </Box>
            </Tooltip>
          </Grid>

          <Grid
            className="clickAway"
            container
            sx={{ justifyContent: 'center', alignItems: 'center' }}
            size={{
              xs: gridLayout.xs.infoIcon,
              sm: gridLayout.sm.infoIcon,
            }}
          >
            <InfoDialogIcon variable={variable as IFeatureOrFamily} type="FF" />
          </Grid>
          <Grid
            className="clickAway"
            container
            sx={{ justifyContent: 'center', alignItems: 'center' }}
            onBlur={onLeave}
            size={{
              xs: gridLayout.xs.qty,
              sm: gridLayout.sm.qty,
            }}
          >
            <Tooltip title={isFeatureReadOnly ? readOnlyTooltipText : ''}>
              <Box>
                <IconButton
                  className="iconCheck"
                  disabled={buttonProps.dec.disabled}
                  onClick={() =>
                    onValueUpdate({
                      increment: false,
                      internalNumericValue: internalNumericValue ?? '',
                      variable,
                      step,
                      setRangeValidity,
                      debounceOnChange,
                      setInternalNumericValue,
                    })
                  }
                >
                  <RemoveCircleRounded className={`${buttonProps.dec.className}`} />
                </IconButton>
                <TextField
                  key={`numeric-input-${variable.id}`}
                  className={'numeric-input-field txt-field'}
                  color="primary"
                  type="number"
                  value={internalNumericValue}
                  onChange={onEventChange}
                  onKeyDown={blockInvalidChar}
                  disabled={isFeatureReadOnly}
                  slotProps={{
                    htmlInput: {
                      title: '',
                      style: { width: `${upperValue > 5 ? upperValue + 0.5 : 5}ch` },
                    },
                  }}
                />
                <IconButton
                  className="updateIcon"
                  data-testid="updateIcon"
                  disabled={buttonProps.inc.disabled}
                  onClick={() =>
                    onValueUpdate({
                      increment: true,
                      internalNumericValue: internalNumericValue ?? '',
                      variable,
                      step,
                      setRangeValidity,
                      debounceOnChange,
                      setInternalNumericValue,
                    })
                  }
                >
                  <AddCircleRounded className={`${buttonProps.inc.className}`} />
                </IconButton>
              </Box>
            </Tooltip>
          </Grid>
          {isSubModel ? (
            <Grid
              container
              size={{
                xs: gridLayout.xs.optional,
                sm: gridLayout.sm.optional,
              }}
            />
          ) : (
            <Grid
              container
              sx={{ alignItems: 'center', justifyContent: 'center' }}
              size={{
                xs: gridLayout.xs.optional,
                sm: gridLayout.sm.optional,
              }}
            >
              <OptionalControl
                variable={variable}
                onOptionalChange={onOptionalChange}
                featureFamily={variable}
                disabled={disabled}
                featureFlag={featureFlags!.Optional}
              />
            </Grid>
          )}
          <Grid
            container
            sx={{ alignItems: 'right', justifyContent: 'right' }}
            size={{
              xs: gridLayout.xs.price,
              sm: gridLayout.sm.price,
            }}
          >
            <Box sx={{ display: { xs: 'inline', sm: 'none' }, textAlign: 'center' }}>
              {t('labels.listPrice')}:&nbsp;
            </Box>
            <Price price={variable.price} isBundled={variable.bundleInfo?.isBundled} />
          </Grid>
        </Grid>
      </ClickAwayListener>

      {validity.valid ? null : (
        <Tooltip title={validityHintText}>
          <FormHelperText className="validation-error">{validityHintText}</FormHelperText>
        </Tooltip>
      )}
      {rangeValidity ? (
        <Tooltip title={validityHintText}>
          <FormHelperText className="validation-error success">
            {t('errorMessages.rangeMessage')}: {rangeValidity}
          </FormHelperText>
        </Tooltip>
      ) : null}
    </>
  );
};
